import React from 'react';
import { Button } from '@vaiu/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@vaiu/ui/card';
import { Switch } from '@vaiu/ui/switch';
import { Badge } from '@vaiu/ui/badge';
import { Alert, AlertDescription } from '@vaiu/ui/alert';
import { Bell, BellOff, CheckCircle, XCircle, AlertTriangle, TestTube, Smartphone, Globe } from 'lucide-react';
import { useNotifications } from '@/context/NotificationContext';

const NotificationSettings: React.FC = () => {
  const {
    isSupported,
    status,
    isLoading,
    subscribe,
    unsubscribe,
    requestPermission,
    testNotifications,
    refreshStatus
  } = useNotifications();

  const getPermissionBadge = () => {
    switch (status.permission) {
      case 'granted':
        return <Badge variant="default" className="bg-green-100 text-green-800"><CheckCircle className="w-3 h-3 mr-1" />Granted</Badge>;
      case 'denied':
        return <Badge variant="destructive"><XCircle className="w-3 h-3 mr-1" />Denied</Badge>;
      default:
        return <Badge variant="secondary"><AlertTriangle className="w-3 h-3 mr-1" />Not Set</Badge>;
    }
  };

  const getStatusIcon = () => {
    if (status.vapidSubscribed || status.fcmSubscribed) {
      return <Bell className="w-5 h-5 text-green-600" />;
    }
    return <BellOff className="w-5 h-5 text-gray-400" />;
  };

  const getNotificationMethodBadge = (method: 'vapid' | 'fcm', active: boolean) => {
    const config = {
      vapid: { 
        label: 'VAPID', 
        icon: Globe, 
        description: 'Standard Web Push'
      },
      fcm: { 
        label: 'FCM', 
        icon: Smartphone, 
        description: 'Firebase Cloud Messaging'
      }
    };

    const { label, icon: Icon, description } = config[method];
    
    return (
      <div className="flex items-center space-x-2">
        <Badge variant={active ? "default" : "secondary"} className={active ? "bg-green-100 text-green-800" : ""}>
          <Icon className="w-3 h-3 mr-1" />
          {label}
        </Badge>
        <span className="text-xs text-muted-foreground hidden sm:inline">
          {description}
        </span>
      </div>
    );
  };

  const isAnySubscribed = status.vapidSubscribed || status.fcmSubscribed;

  const handleToggleNotifications = async () => {
    if (isAnySubscribed) {
      const results = await unsubscribe();
      console.log('Unsubscribe results:', results);
    } else {
      // First request permission if needed
      if (status.permission !== 'granted') {
        const newPermission = await requestPermission();
        if (newPermission !== 'granted') {
          return;
        }
      }
      const results = await subscribe();
      console.log('Subscribe results:', results);
    }
    
    // Refresh status after operation
    await refreshStatus();
  };

  const handleTestNotifications = async () => {
    const results = await testNotifications();
    console.log('Test results:', results);
  };

  if (!isSupported) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <BellOff className="w-5 h-5" />
            <span>Push Notifications</span>
          </CardTitle>
          <CardDescription>
            Receive instant updates about your transactions and tokens
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Alert>
            <AlertTriangle className="w-4 h-4" />
            <AlertDescription>
              Push notifications are not supported in this browser. Please use a modern browser like Chrome, Firefox, or Safari.
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center space-x-2">
          {getStatusIcon()}
          <span>Push Notifications</span>
          {getPermissionBadge()}
        </CardTitle>
        <CardDescription>
          Receive instant updates about your transactions, token redemptions, and payments
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Current Status */}
        <div className="flex items-center justify-between">
          <div className="space-y-0.5">
            <div className="text-sm font-medium">
              Push Notifications
            </div>
            <div className="text-sm text-muted-foreground">
              {isAnySubscribed 
                ? "You're receiving notifications" 
                : "Enable to get instant updates"
              }
            </div>
          </div>
          <Switch
            checked={isAnySubscribed}
            onCheckedChange={handleToggleNotifications}
            disabled={isLoading || status.permission === 'denied'}
          />
        </div>

        {/* Notification Methods Status */}
        {(status.vapidSubscribed || status.fcmSubscribed) && (
          <div className="space-y-2">
            <div className="text-sm font-medium">Active Methods</div>
            <div className="space-y-2">
              {getNotificationMethodBadge('vapid', status.vapidSubscribed)}
              {getNotificationMethodBadge('fcm', status.fcmSubscribed)}
            </div>
            {status.vapidSubscribed && status.fcmSubscribed && (
              <div className="text-xs text-muted-foreground">
                ✅ Both methods active for maximum reliability
              </div>
            )}
          </div>
        )}

        {/* Service Worker Status */}
        <div className="flex items-center justify-between">
          <div className="space-y-0.5">
            <div className="text-sm font-medium">Service Worker</div>
            <div className="text-sm text-muted-foreground">
              Background notification support
            </div>
          </div>
          <Badge variant={status.hasServiceWorker ? "default" : "secondary"}>
            {status.hasServiceWorker ? (
              <>
                <CheckCircle className="w-3 h-3 mr-1" />
                Active
              </>
            ) : (
              <>
                <XCircle className="w-3 h-3 mr-1" />
                Inactive
              </>
            )}
          </Badge>
        </div>

        {/* Push Support Status */}
        <div className="flex items-center justify-between">
          <div className="space-y-0.5">
            <div className="text-sm font-medium">Push Support</div>
            <div className="text-sm text-muted-foreground">
              Browser push notification capability
            </div>
          </div>
          <Badge variant={status.supportsPush ? "default" : "secondary"}>
            {status.supportsPush ? (
              <>
                <CheckCircle className="w-3 h-3 mr-1" />
                Supported
              </>
            ) : (
              <>
                <XCircle className="w-3 h-3 mr-1" />
                Not Supported
              </>
            )}
          </Badge>
        </div>

        {/* Browser Permission */}
        <div className="flex items-center justify-between">
          <div className="space-y-0.5">
            <div className="text-sm font-medium">Browser Permission</div>
            <div className="text-sm text-muted-foreground">
              Current permission status
            </div>
          </div>
          <div className="flex items-center space-x-2">
            {getPermissionBadge()}
            {status.permission === 'default' && (
              <Button 
                variant="outline" 
                size="sm" 
                onClick={requestPermission}
                disabled={isLoading}
              >
                Request Permission
              </Button>
            )}
          </div>
        </div>

        {/* Permission Denied Help */}
        {status.permission === 'denied' && (
          <Alert>
            <AlertTriangle className="w-4 h-4" />
            <AlertDescription>
              Notifications are blocked. To enable them:
              <br />
              1. Click the lock/notification icon in your browser's address bar
              <br />
              2. Change notifications from "Block" to "Allow"
              <br />
              3. Refresh this page
            </AlertDescription>
          </Alert>
        )}

        {/* Test Notifications */}
        {isAnySubscribed && (
          <div className="pt-2 border-t">
            <Button 
              variant="outline" 
              onClick={handleTestNotifications}
              disabled={isLoading}
              className="w-full"
            >
              <TestTube className="w-4 h-4 mr-2" />
              Send Test Notifications
            </Button>
            <div className="text-xs text-muted-foreground mt-1 text-center">
              Tests both VAPID and FCM if available
            </div>
          </div>
        )}

        {/* Technical Details (for debugging) */}
        {process.env.NODE_ENV === 'development' && isAnySubscribed && (
          <div className="pt-2 border-t">
            <details className="text-xs">
              <summary className="cursor-pointer text-muted-foreground mb-2">
                Technical Details (Debug)
              </summary>
              <div className="space-y-1 text-muted-foreground bg-muted p-2 rounded">
                <div>VAPID: {status.vapidSubscribed ? '✅' : '❌'}</div>
                <div>FCM: {status.fcmSubscribed ? '✅' : '❌'}</div>
                <div>Permission: {status.permission}</div>
                <div>Service Worker: {status.hasServiceWorker ? '✅' : '❌'}</div>
                <div>Push Support: {status.supportsPush ? '✅' : '❌'}</div>
              </div>
            </details>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default NotificationSettings; 